/**
 * Reports data access: TanStack Query hooks over the typed client, CSV/HTML downloads through the
 * session, and the public capability-link reads (plain fetch, no session — a link is not a login).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionFetch } from '@/lib/api/auth';
import { api, ApiError, unwrap } from '@/lib/api/client';
import type { BlockDef, Period, PublicReport, RunOut, Scope, TvBoard, TvConfig } from './model';

export const reportsKeys = {
  all: ['reports'] as const,
  templates: ['reports', 'templates'] as const,
  list: (p: object) => ['reports', 'list', p] as const,
  report: (id: string) => ['reports', 'report', id] as const,
  preview: (id: string) => ['reports', 'preview', id] as const,
  runs: (id: string) => ['reports', 'runs', id] as const,
  run: (id: string) => ['reports', 'run', id] as const,
  schedule: (id: string) => ['reports', 'schedule', id] as const,
  recipients: (q: string) => ['reports', 'recipients', q] as const,
  shares: (p: object) => ['reports', 'shares', p] as const,
};

export function useTemplates(enabled = true) {
  return useQuery({ queryKey: reportsKeys.templates, queryFn: () => unwrap(api.GET('/api/reports/templates')), staleTime: 30 * 60_000, enabled });
}

export function useReports(p: { q?: string; archived?: boolean }) {
  return useInfiniteQuery({
    queryKey: reportsKeys.list(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/reports', { params: { query: { q: p.q, archived: p.archived, cursor: pageParam, limit: 100 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useReport(id: string) {
  return useQuery({ queryKey: reportsKeys.report(id), queryFn: () => unwrap(api.GET('/api/reports/{report_id}', { params: { path: { report_id: id } } })) });
}

export interface ReportInput {
  name?: string | null;
  template_key?: string | null;
  description?: string | null;
  blocks?: BlockDef[] | null;
  scope?: Scope;
  period?: Period | null;
}

export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportInput) =>
      unwrap(api.POST('/api/reports', { body: { ...body, blocks: body.blocks as unknown as Record<string, unknown>[] | null, scope: body.scope } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', 'list'] }),
  });
}

export interface ReportChanges {
  name?: string;
  description?: string;
  blocks?: BlockDef[];
  scope?: Scope;
  period?: Period;
  archived?: boolean;
  version: number;
}

/** PATCH only what changed, with the version (409 → the caller offers a reload). */
export function useUpdateReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportChanges) =>
      unwrap(
        api.PATCH('/api/reports/{report_id}', {
          params: { path: { report_id: id } },
          body: { ...body, blocks: body.blocks as unknown as Record<string, unknown>[] | undefined },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsKeys.report(id) });
      void qc.invalidateQueries({ queryKey: reportsKeys.preview(id) });
      void qc.invalidateQueries({ queryKey: ['reports', 'list'] });
    },
  });
}

export function useGenerate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/reports/{report_id}/generate', { params: { path: { report_id: id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsKeys.runs(id) });
      void qc.invalidateQueries({ queryKey: reportsKeys.report(id) });
    },
  });
}

/** The blocks computed now (no AI summary, nothing stored). */
export function usePreview(id: string, enabled = true) {
  return useQuery({
    queryKey: reportsKeys.preview(id),
    queryFn: () => unwrap(api.GET('/api/reports/{report_id}/preview', { params: { path: { report_id: id } } })),
    staleTime: 60_000,
    enabled,
  });
}

const busy = (r: RunOut | undefined) => r?.status === 'queued' || r?.status === 'running';

export function useRuns(id: string) {
  return useInfiniteQuery({
    queryKey: reportsKeys.runs(id),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET('/api/reports/{report_id}/runs', { params: { path: { report_id: id }, query: { cursor: pageParam, limit: 50 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Poll while a generation is on its way (the job takes seconds).
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some(busy)) ? 2500 : false),
  });
}

export function useRun(runId: string) {
  return useQuery({
    queryKey: reportsKeys.run(runId),
    queryFn: () => unwrap(api.GET('/api/reports/runs/{run_id}', { params: { path: { run_id: runId } } })),
    refetchInterval: (q) => (busy(q.state.data?.run) ? 2500 : false),
  });
}

export function useSchedule(id: string) {
  return useQuery({
    queryKey: reportsKeys.schedule(id),
    queryFn: () => unwrap(api.GET('/api/reports/{report_id}/schedule', { params: { path: { report_id: id } } })),
  });
}

export interface ScheduleInput {
  cadence: 'weekly' | 'monthly' | 'off';
  weekday: number;
  day_of_month: number;
  hour: number;
  recipient_user_ids: string[];
}

export function usePutSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScheduleInput) => unwrap(api.PUT('/api/reports/{report_id}/schedule', { params: { path: { report_id: id } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsKeys.schedule(id) });
      void qc.invalidateQueries({ queryKey: reportsKeys.report(id) });
      void qc.invalidateQueries({ queryKey: ['reports', 'list'] });
    },
  });
}

export function useRecipients(q: string, enabled = true) {
  return useQuery({
    queryKey: reportsKeys.recipients(q),
    queryFn: () => unwrap(api.GET('/api/reports/recipients', { params: { query: { q: q || undefined, limit: 200 } } })),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useShares(p: { purpose?: 'report_view' | 'tv_leaderboard'; report?: string; includeRevoked?: boolean }, enabled = true) {
  return useInfiniteQuery({
    queryKey: reportsKeys.shares(p),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/reports/shares', {
          params: { query: { purpose: p.purpose, report: p.report, include_revoked: p.includeRevoked, cursor: pageParam, limit: 100 } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export interface ShareInput {
  purpose: 'report_view' | 'tv_leaderboard';
  report_id?: string;
  run_id?: string;
  tv?: TvConfig;
  label: string;
  ttl_days: number;
  show_names: boolean;
}

export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShareInput) => unwrap(api.POST('/api/reports/shares', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', 'shares'] }),
  });
}

export function useRevokeShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/reports/shares/{share_id}/revoke', { params: { path: { share_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', 'shares'] }),
  });
}

/** GET a file through the session and hand it to the browser as a download. */
export async function downloadFile(path: string, params: Record<string, string | string[] | number | undefined>, filename: string): Promise<void> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    for (const x of Array.isArray(v) ? v : [v]) qs.append(k, String(x));
  }
  const res = await sessionFetch(`${path}${qs.size ? `?${qs}` : ''}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new ApiError(res.status, body.detail);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * The run's print-ready HTML (server-rendered, escaped, no script) in a new tab, where the
 * browser's print makes the PDF. A CSP meta tag keeps scripts out of the blob document too.
 */
export async function openPrintable(runId: string): Promise<void> {
  const res = await sessionFetch(`/api/reports/runs/${encodeURIComponent(runId)}/html`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new ApiError(res.status, body.detail);
  }
  const csp = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">`;
  const html = (await res.text()).replace(/<head>/i, `<head>${csp}`);
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function publicGet<T>(path: string): Promise<T> {
  // No session, no Authorization header: the token in the path is the only credential.
  const res = await fetch(path, { credentials: 'omit', headers: { Accept: 'application/json' }, referrerPolicy: 'no-referrer' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new ApiError(res.status, body.detail);
  }
  return (await res.json()) as T;
}

export function usePublicReport(token: string) {
  return useQuery({
    queryKey: ['public', 'report', token],
    queryFn: () => publicGet<PublicReport>(`/api/public/reports/${encodeURIComponent(token)}`),
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });
}

export function usePublicTv(token: string) {
  return useQuery({
    queryKey: ['public', 'tv', token],
    queryFn: () => publicGet<TvBoard>(`/api/public/tv/${encodeURIComponent(token)}`),
    // The board refreshes itself on the server's cadence; keep showing the last one on a blip.
    refetchInterval: (q) => (q.state.data?.refresh_s ?? 300) * 1000,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 5,
  });
}
